import { parseDecimal } from "@/lib/formatters"
import type { StatsBucket } from "@/features/staff/stats/types"

/**
 * The trend behind a KPI figure, at thumbnail size.
 *
 * Neutral ink, never a hue. A single series has no identity to encode, and a
 * sales line drawn in red reads as an alarm the moment it goes down — the data
 * does not have an opinion and the stroke must not lend it one. It is also the
 * only treatment that survives grayscale printing and every kind of colour
 * blindness.
 *
 * `aria-hidden`: the figure beside it is the accessible value, and a decorative
 * polyline announced to a screen reader is noise.
 */
export function Sparkline({ buckets }: { buckets: StatsBucket[] }) {
  const values = buckets.map((bucket) => parseDecimal(bucket.subtotal))
  if (values.length < 2) return null

  const max = Math.max(...values)
  const min = Math.min(...values)
  const span = max - min

  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 60
      // A flat series sits on the midline rather than collapsing onto the floor.
      const y = span === 0 ? 11 : 20 - ((value - min) / span) * 18
      return `${Math.round(x * 10) / 10},${Math.round(y * 10) / 10}`
    })
    .join(" ")

  return (
    <svg
      viewBox="0 0 60 22"
      width="60"
      height="22"
      fill="none"
      aria-hidden="true"
      className="shrink-0 overflow-visible"
    >
      <polyline
        points={points}
        className="stroke-foreground"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
